<template>
    <div class="template">
        <input ref="uploadFile" type="file" :accept="extensions()" maxlength="1000" class="form-control" @change="uploadFile" :readonly="readonly" :disabled="readonly">
        <div ref="progressBar" class="progress form-control">
            <div class="progress-bar" role="progressbar" :style="progressBarStyle" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .template {
        position:relative;
        .progress {
            z-index:-1;// use z-index to hide/show so we can use the containing div's dimensions automatically
            position: absolute;
            height:100%;
            width:100%;
            left:0; top: 0; right: 0; bottom: 0;
        }
    }
</style>

<script lang="ts">

import { UploadDto } from "@/shared/models/UploadDto";
import { DataRequest } from "@/shared/support/Data";
import Toast from "@/shared/support/Toast";
import Events from "@/shared/support/Events";
import { defineComponent } from "vue";
import { confirmOk } from "./AlertDialog.vue";

export default defineComponent({
    props: {
        uploadType: { type: Number, required: true, default: 0 },
        uploadKey: { type: [String, Number], required: true, default: "" },
        allowedExtensions: { type: Array as () => string[], required: false, default: () => [] },
        showSuccessUpload: { type: Boolean, required: false, default: true },
        clearAfterUpload: { type: Boolean, required: false, default: true },
        maxSize: { type: Number, required: false, default: 100000000 },
        readonly: { type: Boolean, required: false, default: false },
    },
    emits: ["uploadfile-file-uploaded", "uploading-start", "uploading-end"],
    data() {
        return {
            uploading: false,
            uploadPercentStore: 0,
        };
    },
    computed: {
        uploadPercent: {
            get(): number {
                return this.uploadPercentStore;
            },
            set(val: number): void {
                this.uploadPercentStore = val;
            },
        },
        progressBarStyle(): any {
            return {
                width: `${this.uploadPercent}%`
            };
        },
    },
    methods: {
        clear(): void {
            (this.$refs.uploadFile as HTMLInputElement).value = "";
        },
        extensions(): string {
            if (this.allowedExtensions && this.allowedExtensions.length > 0) {
                let s = "";
                for (const ext of this.allowedExtensions)
                    s += `.${ext},`;
                return s.substring(0, s.length - 1);
            }
            return "";
        },
        uploadFile(): void {
            const file = this.getUploadFile();
            if (!file) return;

            const ix = file.name.lastIndexOf(".");
            if (ix >= 0) {
                const extension = file.name.substring(ix + 1, file.name.length).toLowerCase();
                if (this.allowedExtensions.length && this.allowedExtensions.indexOf(extension) < 0) {
                    (this.$refs.uploadFile as HTMLInputElement).value = "";
                    confirmOk(`The file extension "${extension}" (type ${file.type}) is not allowed.`);
                    return;
                }
            }
            if (file.size > this.maxSize) {
                (this.$refs.uploadFile as HTMLInputElement).value = "";
                confirmOk(`The file "${file.name}" exceeds the maximum file size limit.`);
                return;
            }

            this.uploading = true;
            Events.bubbleEvent(this as any, "uploading-start");
            (this.$refs.progressBar as HTMLDivElement).style.zIndex = "0"; // hide file input to expose progress bar
            this.uploadPercent = 0;

            const dr = new DataRequest();
            dr.$postFile<UploadDto>("/Service/Uploads/UploadFile", { type: this.uploadType, key: this.uploadKey }, file, file.name, (ev: ProgressEvent): any|null => {
                const total = ev.total;
                const position = ev.loaded;
                if (ev.lengthComputable)
                    this.uploadPercent = Math.ceil(position / total * 100);
            })
                .then((result: UploadDto): void => {
                    (this.$refs.progressBar as HTMLDivElement).style.zIndex = "-1";
                    if (this.clearAfterUpload)
                        (this.$refs.uploadFile as HTMLInputElement).value = "";
                    this.uploading = false;
                    Events.bubbleEvent(this as any, "uploading-end");
                    if (result.UpdateResult.Success) {
                        if (this.showSuccessUpload)
                            Toast.success(result.UpdateResult.Message || `File "${file.name}" successfully uploaded.`);
                        this.$emit("uploadfile-file-uploaded", file.name, result);
                    } else {
                        confirmOk(result.UpdateResult.Message || "File upload failed!");
                    }
                })
                .catch((reason: any): void => {
                    (this.$refs.progressBar as HTMLDivElement).style.zIndex = "-1";
                    (this.$refs.uploadFile as HTMLInputElement).value = "";
                    this.uploading = false;
                    Events.bubbleEvent(this as any, "uploading-end");
                });
        },
        getUploadFile(): File | null {
            const uploadFileRef = this.$refs.uploadFile as HTMLInputElement;
            return uploadFileRef && uploadFileRef.files && uploadFileRef.files[0];
        },
    },
});

</script>
